import type { EvidenceItem } from '~/models/rulesOpinion'
import type { Candidate } from '../candidate'
import type { DerivedFacts, PhaseFacts } from '../facts'
import type { NutritionContext } from '../inputs/snapshot'
import type { AdherenceEvidence } from '../prioritize'
import type { RulesThresholds } from '../thresholds'
import { RULE_IDS } from '../version'

/**
 * Evidence construction and the two cross-phase rules, shared by all five
 * families (Checkpoint 2.4).
 *
 * Extracted from `fatLoss.ts` rather than copied into four new files. The
 * observations are identical whatever the phase — what differs is what they
 * *mean*, which is the whole point of a phase-aware engine (implementation-plan
 * §4). Duplicating the evidence text would let two phases drift into describing
 * the same number differently.
 *
 * **No numeric literal appears in any ruleset**, here or in the five families.
 * Every threshold resolves from `rules/thresholds.ts` with the decision that
 * authorised it (structural rule 5).
 */

/**
 * The observed facts behind every candidate.
 *
 * Each item carries the unrounded value and its unit alongside the sentence, so
 * the JSON export serializes full precision rather than re-reading numbers out
 * of the prose (D-039).
 */
export function evidenceFor(facts: DerivedFacts): EvidenceItem[] {
  const items: EvidenceItem[] = [weightEvidence(facts)]
  const waist = waistEvidence(facts)
  if (waist) items.push(waist)
  return items
}

function weightEvidence(facts: DerivedFacts): EvidenceItem {
  const change = facts.weight.weeklyChangePercent
  const kg = facts.weight.weeklyChangeKg

  if (change === undefined || kg === undefined) {
    return {
      kind: 'weight',
      statement: `Seven-day average based on ${facts.weight.entryCount} weigh-ins.`,
    }
  }

  return {
    kind: 'weight',
    statement: `Seven-day average changed ${formatSigned(kg)} kg (${formatSigned(change)}%) against the previous seven days.`,
    value: change,
    unit: 'percentPerWeek',
  }
}

function waistEvidence(facts: DerivedFacts): EvidenceItem | undefined {
  if (!facts.waist.available || facts.waist.changeCm === undefined) return undefined
  return {
    kind: 'waist',
    statement: `Waist changed ${formatSigned(facts.waist.changeCm)} cm since the previous measurement.`,
    value: facts.waist.changeCm,
    unit: 'cm',
  }
}

export function watchItemsFor(facts: DerivedFacts): string[] {
  const items: string[] = []
  if (facts.waist.latestQualityWarning) {
    items.push(
      'Your three waist readings varied more than the tolerance, so this waist figure is less reliable than usual.',
    )
  }
  return items
}

export function missingEvidenceFor(facts: DerivedFacts): string[] {
  const missing: string[] = []
  if (!facts.waist.available) {
    missing.push('A waist measurement — waist trend is the strongest single signal for progress')
  } else if (facts.waist.changeCm === undefined) {
    missing.push('A second waist measurement, so the waist trend can be compared week to week')
  }
  if (!facts.recovery.available) {
    missing.push('Recovery ratings for this week')
  }
  if (facts.strength.liftsTracked === 0) {
    missing.push('Sentinel-lift performance, which would show whether strength is holding')
  }
  return missing
}

/**
 * Signed to the first decimal, with a true minus sign.
 *
 * Direction is carried in the text so it survives greyscale, colour-vision
 * differences and a screen reader (D-053). This is inside the domain rather
 * than at the presentation boundary because the string IS the evidence, not a
 * rendering of it; nothing here feeds back into a calculation (D-041).
 */
export function formatSigned(value: number): string {
  const rounded = Math.abs(value) < 0.05 ? 0 : value
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : ''
  return `${sign}${Math.abs(rounded).toFixed(1)}`
}

/* ---- Cross-phase rules --------------------------------------------------- */

/**
 * Recovery is deteriorating (D-066).
 *
 * Either condition fires it: any single item at or below the concern level, or
 * the five-item mean sitting far enough below its own trailing four-week
 * average. One badly wrong thing matters even when the average looks fine, and
 * a broad slide matters even when nothing is individually alarming.
 *
 * This outranks every phase's ordinary progress logic, in every phase, which is
 * what `safetyAndRecovery` leading `RULE_FAMILY_PRECEDENCE` means.
 */
export function recoveryConcern(
  facts: DerivedFacts,
  thresholds: RulesThresholds,
): Candidate | undefined {
  const { lowestItemScore, meanDropFromFourWeekAverage } = facts.recovery
  if (!facts.recovery.available) return undefined

  const singleItemLow =
    lowestItemScore !== undefined &&
    lowestItemScore <= thresholds.recovery.singleRatingConcernAtOrBelow
  const meanFallen =
    meanDropFromFourWeekAverage !== undefined &&
    meanDropFromFourWeekAverage >= thresholds.recovery.meanDropBelowFourWeekAverage

  if (!singleItemLow && !meanFallen) return undefined

  return {
    category: 'easeTheCurrentPhase',
    primaryAction:
      'Ease off this week and prioritise recovery. How you are feeling has dropped enough that pushing on is more likely to cost you than gain you anything.',
    supportingActions: ['Protect sleep before anything else.'],
    ruleId: RULE_IDS.recoveryDeteriorating,
    family: 'safetyAndRecovery',
    leadingEvidence: 'strength',
    evidence: [...evidenceFor(facts), recoveryEvidence(facts)],
    watchItems: [
      'Recovery usually falls before strength does. Catching it here is the cheap moment.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Carry on with the current plan',
        reason:
          'Recovery is the first thing to protect. Progress made while recovery is falling is usually given back.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

function recoveryEvidence(facts: DerivedFacts): EvidenceItem {
  return {
    kind: 'dataQuality',
    statement:
      facts.recovery.meanScore === undefined
        ? 'Recovery was rated this week.'
        : `Recovery averaged ${facts.recovery.meanScore.toFixed(1)} out of 5 across the five items.`,
    ...(facts.recovery.meanScore === undefined
      ? {}
      : { value: facts.recovery.meanScore, unit: 'ratingOutOfFive' }),
  }
}

/**
 * Strength is declining across several lifts, for several weeks (D-066, D-069).
 *
 * All three conditions must hold — size, breadth and duration. One lift down
 * one week is a bad session; two lifts down for two weeks running is a trend,
 * and it is a reason to ease whatever phase is running.
 */
export function strengthConcern(
  facts: DerivedFacts,
  thresholds: RulesThresholds,
): Candidate | undefined {
  const { liftsDecliningThisWeek, consecutiveWeeksDeclining } = facts.strength
  if (liftsDecliningThisWeek < thresholds.strength.liftsRequired) return undefined
  if (consecutiveWeeksDeclining < thresholds.strength.consecutiveWeeksRequired) return undefined

  return {
    category: 'easeTheCurrentPhase',
    primaryAction:
      'Ease the current phase. Several lifts have been below their four-week average for two weeks running, which is the pattern that usually means the plan is costing you strength.',
    supportingActions: ['Keep training, but pull back the hardest sets rather than stopping.'],
    ruleId: RULE_IDS.strengthDeclining,
    family: 'safetyAndRecovery',
    leadingEvidence: 'strength',
    evidence: [
      ...evidenceFor(facts),
      {
        kind: 'dataQuality',
        statement: `${liftsDecliningThisWeek} of ${facts.strength.liftsTracked} tracked lifts are below their four-week average, for ${consecutiveWeeksDeclining} weeks running.`,
        value: liftsDecliningThisWeek,
        unit: 'lifts',
      },
    ],
    watchItems: [
      'Estimated one-rep maxes are estimates. Treat a run of weeks as the signal, never one session.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Push through it',
        reason:
          'Strength has been falling across several lifts for two weeks, which is long enough to be a trend rather than a bad session.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Adherence was too low to judge the plan (D-066, D-007).
 *
 * Fires in every phase: whatever the numbers say, a plan that was not followed
 * was not tested, and the honest first action is to follow it. `unknown` does
 * **not** fire this — nothing was recorded, which is different from something
 * recorded and poor, and saying "improve adherence" to someone who simply did
 * not fill the step in would be scolding them for a blank field.
 */
export function adherenceConcern(
  facts: DerivedFacts,
  adherence: AdherenceEvidence,
): Candidate | undefined {
  /* ONLY `tooLowToJudge`. `unknown` must not fire this: nothing was recorded,
     which is different from something recorded and poor, and telling someone to
     improve their adherence because they left a step blank would be scolding
     them for an empty field. Passing `!isJudgeable(...)` here instead made this
     fire on every week with no adherence recorded — caught by the fat-loss
     tests, which suddenly returned an adherence action for weeks that had none. */
  if (adherence !== 'tooLowToJudge') return undefined

  return {
    category: 'improveAdherenceFirst',
    primaryAction:
      'Focus on following the plan you already have this week, rather than changing it. It has not yet been followed closely enough to tell whether it works.',
    supportingActions: [],
    ruleId: RULE_IDS.adherenceTooLowToJudge,
    family: 'adherence',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: watchItemsFor(facts),
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Change intake or activity',
        reason:
          'The current plan has not been tested yet. Changing it now would be reacting to how closely it was followed, not to whether it works.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * The external authority is set to a rate this phase is not planned for (D-096).
 *
 * **It shows the disagreement and resolves nothing.** Silently adjusting Project
 * Sola's phase to match the other system, or telling the user to change the other
 * system to match Project Sola, would both be this app deciding which of two
 * authorities is right about a number it did not originate. It is not entitled to
 * that: the user chose the external authority, and D-096 makes the numbers theirs.
 *
 * **No threshold is introduced, and that is why the test is "outside the range"
 * rather than "far enough apart".** A tolerance would be a coaching number nobody
 * approved. The phase range is the user's own configured boundary, so comparing
 * against it invents nothing — the app is reporting that two things they set up
 * do not agree.
 *
 * Fires only under an external authority. A `manualTarget` user setting a rate
 * that disagrees with their own phase is one person disagreeing with themselves
 * inside one app, which is an editing problem rather than a conflict between
 * systems, and `configuredGoalRatePercent` is not collected for them.
 */
export function nutritionSourceConflict(
  facts: DerivedFacts,
  phase: PhaseFacts,
): Candidate | undefined {
  if (facts.nutrition.authority !== 'external') return undefined

  const configured = facts.nutrition.configuredGoalRatePercent
  if (configured === undefined) return undefined
  if (configured >= phase.targetMinPercent && configured <= phase.targetMaxPercent) return undefined

  return {
    category: 'doNotAdjustYet',
    primaryAction:
      'Check the goal rate in the system that sets your targets. It is configured for a different weekly rate than this phase is planned for, and Project Sola will not change either one for you.',
    supportingActions: [
      'Decide which is right, change it where it is set, and update Project Sola so the two agree.',
    ],
    ruleId: RULE_IDS.nutritionSourceConflict,
    family: 'nutritionSourceConflict',
    leadingEvidence: 'weight',
    evidence: [
      ...evidenceFor(facts),
      {
        kind: 'phase',
        statement: `Your targets are set for ${formatSigned(configured)}% a week, and this phase is planned for ${formatSigned(phase.targetMinPercent)}% to ${formatSigned(phase.targetMaxPercent)}%.`,
        value: configured,
        unit: 'percentPerWeek',
      },
    ],
    watchItems: [
      'Until they agree, a week judged against this phase is being measured against a rate your plan was never set to.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: "Change this phase to match the other system's rate",
        reason:
          'Project Sola did not set either number and will not overrule the authority you chose. Which one moves is your decision.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Redirect an action to wherever the numbers actually live (D-096).
 *
 * **Applied once, in `prioritize.ts`, rather than inside twelve rules.** The
 * D-007 filter established the pattern and the argument is the same: a rule that
 * has to remember to phrase itself correctly for an external authority is a rule
 * that will eventually forget, and the next ruleset written will never have heard
 * of the requirement at all.
 *
 * **The rule identifier does not change** — this is wording, not a different
 * conclusion. `phaseMatrix.test.ts` asserts by identifier precisely so that a
 * change of this kind cannot look like a change of rule.
 *
 * The two categories are the two that ask the user to change something they eat
 * or do; every other category tells them to keep going, gather data, or make a
 * decision about the phase itself, and none of those needs redirecting.
 *
 * **The provider is never named here.** The sentence says "where your targets are
 * set", not "in MacroFactor", because this string is stored on the opinion and
 * serialized into every export, while the provider label gates on `includeNotes`.
 */
export function redirectToNutritionAuthority(
  candidate: Candidate,
  nutrition: NutritionContext,
): Candidate {
  if (nutrition.authority !== 'external') return candidate
  if (
    candidate.category !== 'considerOneSmallAdjustment' &&
    candidate.category !== 'easeTheCurrentPhase'
  ) {
    return candidate
  }

  return {
    ...candidate,
    primaryAction: `${candidate.primaryAction} Make that change where your targets are set, then update Project Sola with the new numbers — it does not set them.`,
  }
}

/**
 * The phase has reached its planned end (D-020).
 *
 * Warns and changes nothing. The transition is always the user's explicit
 * decision — the engine never applies one, in any phase.
 */
export function plannedEndReached(facts: DerivedFacts, phase: PhaseFacts): Candidate | undefined {
  if (!phase.atOrPastPlannedEnd) return undefined

  return {
    category: 'endOrTransitionThePhase',
    primaryAction:
      'This phase has reached the end date you planned. Decide whether to move to the next phase, extend this one, or revise the plan — Project Sola will not change it for you.',
    supportingActions: [],
    ruleId: RULE_IDS.phasePlannedEndReached,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: watchItemsFor(facts),
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Continue automatically into another phase',
        reason:
          'A phase transition is always an explicit decision. Nothing about your plan changes until you choose it.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}
